import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_VAT_RATE,
  INVOICE_DUE_DAYS,
  VAT_RATES,
  computeInvoiceDueDate,
  isInvoiceDueDays,
  summarizeInvoiceVat,
  type InvoiceDetailDto,
  type InvoiceDueDays,
  type InvoiceLineInput,
  type VatRate,
} from "@tc/shared";
import { Button, Card, Field, IconButton, Select, TextInput } from "@/components/ui";
import { recordsApi } from "./api";
import { eur } from "./FacturaView";

interface Row {
  section: string;
  description: string;
  qty: string;
  unitPrice: string;
  vatRate: VatRate;
}

function toRow(l: InvoiceDetailDto["lineItems"][number]): Row {
  return {
    section: l.section ?? "",
    description: l.description,
    qty: String(l.qty),
    unitPrice: String(l.unitPrice),
    vatRate: l.vatRate,
  };
}

const BLANK: Row = { section: "", description: "", qty: "1", unitPrice: "0", vatRate: DEFAULT_VAT_RATE };

const VAT_OPTIONS = VAT_RATES.map((r) => ({ value: String(r), label: `${r}%` }));

/** Map an editor row to the line input the API expects (numbers parsed; blanks → null). */
function toInput(r: Row): InvoiceLineInput {
  return {
    section: r.section.trim() || null,
    lineDate: null,
    description: r.description.trim(),
    qty: Number(r.qty) || 0,
    unitPrice: Number(r.unitPrice) || 0,
    vatRate: r.vatRate,
  };
}

/**
 * Draft factura editor (TW-3527) — back-office only. The admin sets the invoice's issue date and
 * payment TERMS (TRAV-2: a 0/7/14/30/60/90-day select — the due date itself is stamped at issue as
 * issue date + terms) and edits the line items (optional section heading, description, quantity,
 * ex-VAT unit price and the per-line VAT rate). A live breakdown previews the subtotal / IVA /
 * total. Saving PUTs the whole set (replace-set) + the date/terms and hands the re-priced detail
 * back so the read-only factura beneath re-renders. Only a Draft is editable — the parent hides
 * this once the invoice is issued.
 */
export function FacturaLineEditor({
  invoice,
  onSaved,
}: {
  invoice: InvoiceDetailDto;
  onSaved: (detail: InvoiceDetailDto) => void;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>(() => invoice.lineItems.map(toRow));
  const [issueDate, setIssueDate] = useState(invoice.issueDate ?? "");
  const [dueDays, setDueDays] = useState<InvoiceDueDays>(() =>
    isInvoiceDueDays(invoice.dueDays) ? invoice.dueDays : 30,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Informational: the Vencimiento this Draft will get if issued today (or on its set issue date).
  const duePreview = computeInvoiceDueDate(issueDate || new Date().toISOString(), dueDays)
    .split("-")
    .reverse()
    .join("/");

  const totals = useMemo(
    () =>
      summarizeInvoiceVat(
        rows.map((r) => ({ qty: Number(r.qty) || 0, unitPrice: Number(r.unitPrice) || 0, vatRate: r.vatRate })),
      ),
    [rows],
  );

  function update(i: number, patch: Partial<Row>) {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }

  async function save() {
    const lines = rows.map(toInput).filter((l) => l.description.length > 0);
    setSaving(true);
    setError(null);
    try {
      const detail = await recordsApi.saveInvoiceLines(invoice.tripId, invoice.id, {
        lines,
        issueDate: issueDate || null,
        dueDays,
      });
      onSaved(detail);
      setRows(detail.lineItems.map(toRow));
      setIssueDate(detail.issueDate ?? "");
      if (isInvoiceDueDays(detail.dueDays)) setDueDays(detail.dueDays);
    } catch {
      setError(t("factura.editor.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card style={{ marginBottom: 18 }}>
      <div className="set-section-head">
        <h2>{t("factura.editor.title")}</h2>
        <div className="sub">{t("factura.editor.sub")}</div>
      </div>

      <div style={{ display: "flex", gap: 16, marginTop: 14 }}>
        <div style={{ flex: "0 0 25%" }}>
          <Field label={t("factura.editor.issueDate")}>
            <TextInput type="date" value={issueDate} onChange={setIssueDate} />
          </Field>
        </div>
        <div style={{ flex: "0 0 25%" }}>
          <Field
            label={t("factura.editor.terms")}
            hint={t("factura.editor.duePreview", { date: duePreview })}
          >
            <Select
              value={String(dueDays)}
              options={INVOICE_DUE_DAYS.map((d) => ({
                value: String(d),
                label: d === 0 ? t("factura.editor.termsOnReceipt") : t("factura.editor.termsDays", { days: d }),
              }))}
              onChange={(v) => {
                const n = Number(v);
                if (isInvoiceDueDays(n)) setDueDays(n);
              }}
            />
          </Field>
        </div>
      </div>

      <table className="table fac-editor" style={{ marginTop: 14 }}>
        <thead>
          <tr>
            <th style={{ width: "22%" }}>{t("factura.editor.section")}</th>
            <th>{t("factura.editor.description")}</th>
            <th style={{ width: 96 }}>{t("factura.editor.qty")}</th>
            <th style={{ width: 150 }}>{t("factura.editor.unitPrice")}</th>
            <th style={{ width: 130 }}>{t("factura.editor.vat")}</th>
            <th style={{ width: 44 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>
                <TextInput value={r.section} onChange={(v) => update(i, { section: v })} placeholder="—" />
              </td>
              <td>
                <TextInput value={r.description} onChange={(v) => update(i, { description: v })} />
              </td>
              <td>
                <TextInput type="number" min={0} value={r.qty} onChange={(v) => update(i, { qty: v })} />
              </td>
              <td>
                <TextInput type="number" min={0} value={r.unitPrice} onChange={(v) => update(i, { unitPrice: v })} />
              </td>
              <td>
                <Select
                  value={String(r.vatRate)}
                  options={VAT_OPTIONS}
                  onChange={(v) => update(i, { vatRate: Number(v) as VatRate })}
                />
              </td>
              <td>
                <IconButton
                  name="trash"
                  label={t("factura.editor.remove")}
                  onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="row-between" style={{ marginTop: 12, alignItems: "flex-start" }}>
        <Button variant="ghost" size="sm" icon="plus" onClick={() => setRows((rs) => [...rs, { ...BLANK }])}>
          {t("factura.editor.addLine")}
        </Button>
        <div className="fac-totals" style={{ fontVariantNumeric: "tabular-nums" }}>
          <div className="row-between">
            <span className="muted">{t("factura.editor.subtotal")}</span>
            <span>{eur(totals.subtotal, invoice.currency)}</span>
          </div>
          <div className="row-between">
            <span className="muted">{t("factura.editor.iva")}</span>
            <span>{eur(totals.vatTotal, invoice.currency)}</span>
          </div>
          <div className="row-between fac-totals-grand">
            <strong>{t("factura.editor.total")}</strong>
            <strong>{eur(totals.grandTotal, invoice.currency)}</strong>
          </div>
        </div>
      </div>

      <div className="card-foot">
        {error && (
          <span className="foot-note" style={{ color: "var(--danger)" }}>
            {error}
          </span>
        )}
        <Button variant="primary" icon="check" onClick={save} disabled={saving}>
          {t("factura.editor.save")}
        </Button>
      </div>
    </Card>
  );
}
